import { useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useAuthStore } from './store/authStore';
import Login from './pages/auth/Login';
import Home from './pages/Home';
import ChangePassword from './pages/account/ChangePassword';
import NamedRowsPage from './pages/buyers/NamedRowsPage';
import IngredientsPage from './pages/buyers/IngredientsPage';
import FuturesPage from './pages/buyers/FuturesPage';
import HedgingPage from './pages/buyers/HedgingPage';
import RequirementsPage from './pages/buyers/RequirementsPage';
import NotFound from './pages/NotFound';

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-body text-ink-muted" role="status">
        Checking your session
      </p>
    </div>
  );
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Logic Ag staff only: a buyer's own user is sent home. */
export function StaffRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  if (role === 'buyer') return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** One screen instance per buyer and screen, so nothing carries over from Mills to Suppliers or from one buyer to the next. */
function BuyerScreen({ screen }: { screen: 'mills' | 'suppliers' | 'ingredients' | 'futures' | 'hedging' | 'requirements' }) {
  const { slug = '' } = useParams();
  const key = `${screen}:${slug}`;
  if (screen === 'ingredients') return <IngredientsPage key={key} slug={slug} />;
  if (screen === 'futures') return <FuturesPage key={key} slug={slug} />;
  if (screen === 'hedging') return <HedgingPage key={key} slug={slug} />;
  if (screen === 'requirements') return <RequirementsPage key={key} slug={slug} />;
  return <NamedRowsPage key={key} slug={slug} resource={screen} />;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const { checkAuth } = useAuth();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/password"
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyers/:slug/mills"
        element={
          <ProtectedRoute>
            <StaffRoute>
              <BuyerScreen screen="mills" />
            </StaffRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyers/:slug/suppliers"
        element={
          <ProtectedRoute>
            <StaffRoute>
              <BuyerScreen screen="suppliers" />
            </StaffRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyers/:slug/ingredients"
        element={
          <ProtectedRoute>
            <StaffRoute>
              <BuyerScreen screen="ingredients" />
            </StaffRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyers/:slug/futures"
        element={
          <ProtectedRoute>
            <StaffRoute>
              <BuyerScreen screen="futures" />
            </StaffRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyers/:slug/hedging"
        element={
          <ProtectedRoute>
            <StaffRoute>
              <BuyerScreen screen="hedging" />
            </StaffRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyers/:slug/requirements"
        element={
          <ProtectedRoute>
            <StaffRoute>
              <BuyerScreen screen="requirements" />
            </StaffRoute>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
